import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useContext } from "react";
import { useDoLogout } from "src/utils/useDoLogout";
import { WidgetContext } from "src/contexts/WidgetContext";
import { isE2eAuthBypassEnabled } from "src/utils/e2eAuthBypass";

/**
 * A hook that provides a "safe" way to get an access token from Auth0.
 * It wraps getAccessTokenSilently in a try-catch block.
 * If getting the token fails (e.g., due to an invalid refresh token like 403 invalid_grant),
 * it will automatically log out the user using the app's custom logout logic.
 * 
 * NON-LOGGED EXPERIENCE: When isLoggedInAuth0ExperienceGlobal is false, returns mock token
 * 
 * @returns A function `getSafeAccessAuth0Token` which returns the token or null if logout was triggered.
 */
export const useSafeAccessAuth0Token = () => {
  const { getAccessTokenSilently, isAuthenticated: auth0IsAuthenticated, } = useAuth0();
  const doLogOut = useDoLogout();
  const { isLoggedInAuth0ExperienceGlobal,NON_LOGGED_IN_CONFIG } = useContext(WidgetContext);

  const getSafeAccessAuth0Token = useCallback(async (): Promise<string | null> => {
    // --- E2E AUTH BYPASS ---
    // If we are running E2E tests, skip Auth0 completely and return a mock token.
    if (isE2eAuthBypassEnabled()) {
      console.warn("[E2E BYPASS] Injecting mock Auth0 token for E2E testing.");
      return "mock-e2e-token-12345";
    }

    // CRITICAL: If user is authenticated with Auth0, ALWAYS use real token (prevents race condition)
    // This ensures that if you're logged in, we never use mock token even if isLoggedInAuth0ExperienceGlobal hasn't updated yet
    if (auth0IsAuthenticated) {
      try {
        const accessTokenFromAuth0 = await getAccessTokenSilently();
        // console.log("accessTokenFromAuth0 useSafeAccessAuth0Token:", accessTokenFromAuth0);
        return accessTokenFromAuth0;
      } catch (error) {
        console.error("Failed to get access token, session is likely invalid. Logging out.", error);
        await doLogOut();
        return null;
      }
    }

    // If NOT authenticated AND in non-logged experience, return mock token
    if (!isLoggedInAuth0ExperienceGlobal) {
      return NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_ACCESS_TOKEN;
    }

    // If in Auth0 experience but not authenticated, return null (should redirect to login)
    return null;
  }, [getAccessTokenSilently, doLogOut, isLoggedInAuth0ExperienceGlobal, auth0IsAuthenticated, NON_LOGGED_IN_CONFIG]);

  return { getSafeAccessAuth0Token };
};
